/**
 * EPCR → CallData adapter.
 *
 * Bridges the in-app ePCR record (Dexie-backed) into the v2
 * narrative engine's `CallData` slot schema. The adapter reads:
 *
 *   - epcr fields (chief complaint, dispatch, destination, MOI, etc.)
 *   - medications_given table (filtered by epcr_id)
 *   - procedures table (filtered by epcr_id)
 *   - vital_sets table (filtered by epcr_id, sorted by timestamp)
 *
 * …and emits a CallData ready for `assembleNarrative()`. Anything the
 * crew hasn't entered yet renders as the placeholder `____`, which
 * the validator surfaces as a missing-required-point warning.
 *
 * Note: this adapter is intentionally NOT a session-event subscriber.
 * Every Care Matrix tap already writes to the Dexie tables; the
 * narrative engine consumes that source of truth. This keeps the
 * narrative deterministic and re-generatable from the current state
 * of the record at any time.
 */

import { db } from '@/offline/db';
import {
  assembleNarrative, type AssembledDraft,
} from './assembler';
import { selectFormat } from './format-selector';
import {
  RI_CONTROLLED_SUBSTANCES, NON_RESTOCK_BOLD_EXCLUSIONS,
  minScopeForDrug, minScopeForProcedure,
  type CallData, type MedicationAdmin, type ProcedureRecord,
  type VitalsSet, type ProviderLevel,
} from './schema';
import type { EPCR, MedicationGiven, Procedure, VitalSet } from '@/types';

/**
 * Map the cert-level field on the user / ePCR provider to the
 * narrative engine's ProviderLevel union. The DB stores cert as
 * 'EMT' | 'AEMT' | 'EMT-Cardiac' (RI-specific) | 'Paramedic'; the
 * engine adds 'AEMT-Cardiac' as a tier between AEMT and Paramedic.
 */
function toProviderLevel(cert: string | null | undefined): ProviderLevel {
  if (!cert) return 'EMT';
  const c = cert.toUpperCase();
  if (/PARAMEDIC|EMT-P\b/.test(c)) return 'Paramedic';
  if (/AEMT.*CARDIAC|EMT.*CARDIAC|CARDIAC/.test(c)) return 'AEMT-Cardiac';
  if (/AEMT|EMT-I/.test(c)) return 'AEMT';
  return 'EMT';
}

function formatTime(iso: string | null | undefined): string {
  if (!iso) return '____';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '____';
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}${mm}`;
}

function vitalToVitalsSet(v: VitalSet, ctx?: VitalsSet['context']): VitalsSet {
  const bp =
    v.bp_systolic != null && v.bp_diastolic != null
      ? `${v.bp_systolic}/${v.bp_diastolic}`
      : v.bp_systolic != null
        ? `${v.bp_systolic}/?`
        : '____';
  return {
    time: formatTime(v.timestamp),
    bp,
    pulse: v.heart_rate != null ? String(v.heart_rate) : '____',
    rr: v.respiratory_rate != null ? String(v.respiratory_rate) : '____',
    spo2: v.spo2 != null ? String(v.spo2) : '____',
    o2: 'room air',
    context: ctx,
  };
}

function medToAdmin(m: MedicationGiven, performer: string): MedicationAdmin {
  const drug = (m.drug_name ?? '').toUpperCase();
  const dose = m.unit ? `${m.dose} ${m.unit}` : (m.dose ?? '');
  return {
    drug,
    dose,
    route: (m.route ?? '').toUpperCase(),
    time: formatTime(m.timestamp),
    performer,
    response: m.patient_response ?? '',
    excludeFromBold: NON_RESTOCK_BOLD_EXCLUSIONS.has(drug),
    controlledSubstance: RI_CONTROLLED_SUBSTANCES.has(drug),
    min_scope_level: minScopeForDrug(drug),
    auto_capture_vitals: true,
  };
}

function procToRecord(p: Procedure, performer: string): ProcedureRecord {
  return {
    procedure: p.procedure_type,
    location: p.body_site ?? '____',
    attempt: p.attempt_number === 1 ? 'first' : `${p.attempt_number}`,
    outcome:
      p.outcome === 'successful'
        ? 'without complication'
        : p.outcome === 'unsuccessful'
          ? `unsuccessful${p.complications ? ` (${p.complications})` : ''}`
          : 'not attempted',
    performer,
    time: formatTime(p.timestamp),
    complications: p.complications ?? undefined,
    min_scope_level: minScopeForProcedure(p.procedure_type),
    auto_capture_vitals: /12[-\s]?lead|defibrillat|cardiovers/i.test(p.procedure_type),
  };
}

const PERFORMER_BY_LEVEL: Record<ProviderLevel, string> = {
  EMT: 'EMT',
  AEMT: 'AEMT',
  'AEMT-Cardiac': 'AEMT-Cardiac',
  Paramedic: 'paramedic',
};

/**
 * Read the ePCR + linked Dexie tables and produce a CallData ready
 * for the narrative assembler. Pure function over the live record —
 * call this any time to regenerate from current state.
 */
export async function mapEpcrToCallData(
  epcr: EPCR,
  options: { providerLevel?: ProviderLevel; protocolNames?: string[] } = {},
): Promise<CallData> {
  const [meds, procs, vitals] = await Promise.all([
    db.medications_given.where('epcr_id').equals(epcr.id).sortBy('timestamp'),
    db.procedures.where('epcr_id').equals(epcr.id).sortBy('timestamp'),
    db.vital_sets.where('epcr_id').equals(epcr.id).sortBy('timestamp'),
  ]);

  const providerLevel: ProviderLevel = options.providerLevel ?? 'Paramedic';
  const performer = PERFORMER_BY_LEVEL[providerLevel];

  const interventions: MedicationAdmin[] = meds.map((m) => medToAdmin(m, performer));
  const procedures: ProcedureRecord[] = procs.map((p) => procToRecord(p, performer));

  // Default the response on any med that didn't get an explicit
  // patient_response captured. Defensible boilerplate that the
  // narrative engine bolds.
  for (const m of interventions) {
    if (!m.response) m.response = 'Patient tolerated the dose without adverse effect.';
  }

  // Vitals: map first → initial-set; last → pre-handoff (when
  // multiple sets exist). All sets between render as inline captures.
  const vitals_set_1 = vitals[0] ? vitalToVitalsSet(vitals[0], 'initial') : null;
  const vitals_set_2 =
    vitals.length > 1
      ? vitalToVitalsSet(vitals[vitals.length - 1], 'pre-handoff')
      : null;
  const inlineVitals: VitalsSet[] = [];
  if (vitals_set_1) inlineVitals.push(vitals_set_1);
  if (vitals.length > 2) {
    for (let i = 1; i < vitals.length - 1; i += 1) {
      inlineVitals.push(vitalToVitalsSet(vitals[i], 'reassessment'));
    }
  }
  if (vitals_set_2) inlineVitals.push(vitals_set_2);

  // Capture the deviations + protocol skips into the exceptions
  // block. Reading from set_findings._care_matrix_skipped (CareMatrix
  // skip-with-reason flow) and from the procedures table's deviation
  // flag.
  const skipMap =
    (epcr.set_findings?._care_matrix_skipped as Record<string, { stepId: string; reason: string; timestamp: string }> | undefined) ?? {};
  const skippedNotes = Object.values(skipMap).map(
    (s) => `Protocol step skipped at ${formatTime(s.timestamp)}: ${s.reason}.`,
  );
  const deviationNotes = procs
    .filter((p) => p.is_deviation && p.deviation_reason)
    .map(
      (p) =>
        `Deviation at ${formatTime(p.timestamp)} — ${p.procedure_type}: ${p.deviation_reason}.`,
    );
  const exceptions = [...skippedNotes, ...deviationNotes].join(' ').trim() || null;

  // Surface the latest BGL onto bg_value (assessment slot) — the
  // narrative engine renders this in its own sentence.
  const latestVital = vitals[vitals.length - 1];
  const bg_value = latestVital?.blood_glucose != null ? `${latestVital.blood_glucose} mg/dL` : null;
  const ecg_interpretation =
    (epcr.set_findings?.stemi as { interpretation?: string } | undefined)?.interpretation ?? null;

  // Light HPI scaffold from EPCR fields. The full free-text hpi /
  // exam come from the assessment + history tabs which use slightly
  // different field names than the narrative engine's slot schema —
  // we tolerate a few common shapes (bracket-access via unknown cast)
  // and fall back to placeholders for anything missing. The validator
  // surfaces the placeholder as a missing-required-point warning.
  const x = epcr as unknown as Record<string, unknown>;
  const hpi =
    (typeof x.history_present_illness === 'string' && x.history_present_illness) ||
    (typeof x.hpi === 'string' && x.hpi) ||
    '____';
  const pmh = epcr.medical_history?.length ? epcr.medical_history.join(', ') : null;
  const allergiesText =
    epcr.allergies?.length
      ? epcr.allergies.map((a: unknown) => (typeof a === 'string' ? a : (a as { allergen?: string })?.allergen ?? '')).filter(Boolean).join(', ')
      : null;
  const homeMedsArr = (x.home_medications ?? x.medications) as unknown;
  const homeMeds = Array.isArray(homeMedsArr) && homeMedsArr.length
    ? homeMedsArr.map((m) => (typeof m === 'string' ? m : (m as { name?: string })?.name ?? '')).filter(Boolean).join(', ')
    : null;
  const signsText = epcr.signs?.length ? epcr.signs.join(', ') : null;
  const examFindings =
    (typeof x.physical_exam_findings === 'string' && x.physical_exam_findings) ||
    (typeof x.exam_findings === 'string' && x.exam_findings) ||
    signsText ||
    '____';
  const fieldDiagnosis = epcr.primary_impression ?? '____';
  const moi =
    (typeof x.mechanism_of_injury === 'string' && x.mechanism_of_injury) ||
    (typeof x.trauma_penetrating_mechanism === 'string' && x.trauma_penetrating_mechanism) ||
    null;
  const sceneAddress =
    (typeof x.scene_address === 'string' && x.scene_address) ||
    (typeof x.incident_location === 'string' && x.incident_location) ||
    null;
  const receivingName =
    (typeof x.receiving_provider_name === 'string' && x.receiving_provider_name) || null;
  const receivingCred =
    (x.receiving_provider_credential as CallData['receiving_provider_credential']) ?? null;
  const handoffTime =
    (typeof x.transfer_of_care_time === 'string' && x.transfer_of_care_time) ||
    (typeof x.at_hospital_time === 'string' && x.at_hospital_time) ||
    null;

  return {
    provider_level: providerLevel,
    chief_complaint: epcr.chief_complaint ?? '____',
    moi,
    history_source: null,
    hpi,
    pmh,
    family_social_hx: null,
    medications: homeMeds,
    allergies: allergiesText,
    exam_findings: examFindings,
    ecg_interpretation,
    bg_value,
    field_diagnosis: fieldDiagnosis,
    interventions,
    procedures,
    vitals_set_1,
    vitals_set_2,
    vitals: inlineVitals,
    receiving_provider_name: receivingName,
    receiving_provider_credential: receivingCred,
    handoff_time: handoffTime,
    dispatch_location: sceneAddress,
    dispatch_reason: epcr.dispatch_reason ?? epcr.chief_complaint ?? null,
    arrival_finding: null,
    patient_line: null,
    destination: epcr.destination_hospital ?? null,
    alert_activation: null,
    is_transport: epcr.transport_decision === 'transport_this_unit',
    is_interfacility: epcr.report_type === 'interfacility',
    scene_complexity: false,
    status_change: false,
    exceptions,
    protocols_invoked: options.protocolNames ?? [],
    reason_for_transport: null,
    current_status: null,
    specific_ask: null,
  };
}

export interface GenerateOptions {
  providerLevel?: ProviderLevel;
  protocolNames?: string[];
  /** Optional seed for the reservoir (deterministic phrasing). */
  seed?: number;
}

/**
 * End-to-end: ePCR → CallData → assembled narrative. Returns the
 * draft + the format chosen by the selector + a validation report.
 */
export async function generateNarrativeFromEpcr(
  epcr: EPCR,
  options: GenerateOptions = {},
): Promise<AssembledDraft & { callData: CallData }> {
  const callData = await mapEpcrToCallData(epcr, options);
  const draft = assembleNarrative(callData, options.seed);
  // Narrow the format selection if the caller wants to override it.
  void selectFormat;
  return { ...draft, callData };
}
